import { version } from '../../package.json';
import {
  TransportLayer,
  TransportLayerPayloadType,
} from '../braze-provider/braze-provider';
import {
  BrazeCustomEvent,
  BrazeUserAttribute,
} from '../consumers/prescreen-consumer/prescreen-consumer';
import { publishEvents } from '../events';

type Action = 'brazeImpression' | 'brazeClick' | 'brazeClose';
type ContextData = {
  brazePlacement: string;
  brazeTriggerID: string | undefined;
};

type SimpleTrackingFunction = (placement: string, campaignId: string) => void;

type ButtonTrackingFunction = (
  placement: string,
  campaignId: string,
  buttonId: string
) => void;

interface TrackingLibrary {
  trackCardClick: SimpleTrackingFunction;
  trackCardImpression: SimpleTrackingFunction;
  trackInAppMessageImpression: SimpleTrackingFunction;
  trackInAppMessageClick: SimpleTrackingFunction;
  trackInAppMessageButtonClick: SimpleTrackingFunction;
  trackInAppMessageHTMLClick: ButtonTrackingFunction;
  setBrazeCustomAttribute: (userAttribute: BrazeUserAttribute) => void;
  sendBrazeCustomEvent: (customEvent: BrazeCustomEvent) => void;
  sendBrazeAltImageMissingEvent: (customEvent: BrazeCustomEvent) => void;
}

let ttt: TransportLayer | undefined;
const setupTracking = (transportLayer: TransportLayer | undefined): void => {
  ttt = transportLayer;
};

const trackEvent = (action: Action, contextData: ContextData): void => {
  ttt?.event(
    action,
    Object.assign(contextData, { brazeProviderVersion: `${version}` })
  );
};

const getTrackingLibrary = (
  transportLayer: TransportLayer
): TrackingLibrary => {
  setupTracking(transportLayer);
  const publishBrazeEvent = (event: TransportLayerPayloadType): void => {
    transportLayer.publish('stm:braze', event, { fireAndForget: true });
  };

  const trackBrazeInteraction = (
    placement: string,
    eventType: string,
    campaignId: string,
    additionalPayload?: TransportLayerPayloadType
  ): void => {
    let eventName: Action | null = null;
    if (eventType.includes('.clicked')) {
      eventName = 'brazeClick';
    } else if (eventType.includes('.shown')) {
      eventName = 'brazeImpression';
    }
    if (eventName !== null) {
      trackEvent(eventName, {
        brazePlacement: placement,
        brazeTriggerID: campaignId,
        // eslint-disable-next-line node/no-unsupported-features/es-syntax
        ...additionalPayload,
      });

      publishBrazeEvent({
        type: eventType,
        campaignId,
        // eslint-disable-next-line node/no-unsupported-features/es-syntax
        ...additionalPayload,
      });
    }
  };

  const trackCardClick: SimpleTrackingFunction = (placement, campaignId) => {
    trackBrazeInteraction(
      placement,
      publishEvents.CONTENT_CARD_CLICKED,
      campaignId
    );
  };

  const trackCardImpression: SimpleTrackingFunction = (
    placement,
    campaignId
  ) => {
    trackBrazeInteraction(
      placement,
      publishEvents.CONTENT_CARD_SHOWN,
      campaignId
    );
  };

  const trackInAppMessageImpression: SimpleTrackingFunction = (
    placement,
    campaignId
  ) => {
    trackBrazeInteraction(
      placement,
      publishEvents.IN_APP_MESSAGE_SHOWN,
      campaignId
    );
  };
  const trackInAppMessageClick: SimpleTrackingFunction = (
    placement,
    campaignId
  ) => {
    trackBrazeInteraction(
      placement,
      publishEvents.IN_APP_MESSAGE_CLICKED,
      campaignId
    );
  };

  const trackInAppMessageButtonClick: SimpleTrackingFunction = (
    placement,
    campaignId
  ) => {
    trackBrazeInteraction(
      placement,
      publishEvents.IN_APP_MESSAGE_BUTTON_CLICKED,
      campaignId
    );
  };

  const trackInAppMessageHTMLClick: ButtonTrackingFunction = (
    placement,
    campaignId,
    buttonId
  ) => {
    trackBrazeInteraction(
      placement,
      publishEvents.IN_APP_MESSAGE_HTML_CLICKED,
      campaignId,
      { buttonId }
    );
  };

  const setBrazeCustomAttribute = (userAttribute: BrazeUserAttribute): void => {
    publishBrazeEvent({
      type: publishEvents.SET_CUSTOM_USER_ATTRIBUTE,
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      ...userAttribute,
    });
  };

  const sendBrazeCustomEvent = (customEvent: BrazeCustomEvent): void => {
    publishBrazeEvent({
      type: publishEvents.SEND_CUSTOM_EVENT,
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      ...customEvent,
    });
  };

  const sendBrazeAltImageMissingEvent = (
    customEvent: BrazeCustomEvent
  ): void => {
    publishBrazeEvent({
      type: publishEvents.ALT_IMAGE_MISSING,
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      ...customEvent,
    });
  };

  return {
    trackCardClick,
    trackCardImpression,
    trackInAppMessageImpression,
    trackInAppMessageClick,
    trackInAppMessageButtonClick,
    trackInAppMessageHTMLClick,
    setBrazeCustomAttribute,
    sendBrazeCustomEvent,
    sendBrazeAltImageMissingEvent,
  };
};

export {
  ContextData,
  TrackingLibrary,
  setupTracking,
  trackEvent,
  getTrackingLibrary,
};
